import { KeyboardControls, Text } from "@react-three/drei";
import { Perf } from "r3f-perf";
import { Physics } from "@react-three/rapier";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import Ecctrl from "../src/Ecctrl";
import Lights from "./Lights";
import Steps from "./Steps";
import Slopes from "./Slopes";
import RigidObjects from "./RigidObjects";
import FloatingPlatform from "./FloatingPlatform";
import DynamicPlatforms from "./DynamicPlatforms";
import ShotCube from "./ShotCube";
import { useControls, button } from "leva";
import CharacterModel from "./CharacterModel";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import MapScene from "./MapScene";
import PixelationEffect from "./postprocessing/PixelationEffect";
import BananaCat from "./BananaCat";
import { useInteractionStore } from "../src/stores/useInteraction";
import Cake from "./Cake";
import CakeTrigger from "./CakeTrigger";
import Confetti from "./Confetti";
import Balloons from "./Balloons";
import Table from "./Table";
import Paper from "./Paper";
import Gifts from "./Gifts";
import SkeletonCompanion from "./SkeletonCompanion";
import DanceFloor from "./DanceFloor";
import BackupSkeletons from "./BackupSkeletons";
import SignPlane from "./SignPlane";
import Tent from "./Tent";
import Tacos from "./Tacos";
import Orbsman from "./Orbsman";
import SkeletonGreeter from "./SkeletonGreeter";
import { usePlayerHealth } from "../src/stores/usePlayerHealth";
import { useToyCollection } from "../src/stores/useToyCollection";
import { useInventory } from "../src/stores/useInventory";
import Mokey from "./Mokey";
import RufflesCat from "./RufflesCat";
import HeavyMachineGun from "./HeavyMachineGun";
import Teapot from "./Teapot";
import Stairs from "./Stairs";
import BalloonDog from "./BalloonDog";
import CubeModel from "./CubeModel";
import Stairway from "./Stairway";
import MinecraftTorch from "./MinecraftTorch";
import ChessBishop from "./ChessBishop";
import ChessPawn from "./ChessPawn";
import ChessRook from "./ChessRook";
import ChessKing from "./ChessKing";
import ChessKnight from "./ChessKnight";
import ChessQueen from "./ChessQueen";
import TV from "./TV";
import PopCat from "./PopCat";
import ToyCollectible from "./ToyCollectible";
import ExitDoor from "./ExitDoor";
import DistanceCulled from "./DistanceCulled";
import { usePerformanceSettings } from "../src/stores/usePerformanceSettings";

const FALL_DAMAGE_THRESHOLD_MS = 800;
const FALL_DAMAGE_LOCK_DURATION_MS = 500;
const MIN_FALL_SPEED = -0.5;
const HEAVY_MACHINE_GUN_INITIAL_POSITION = [-35.57, -0.07, -24.45];

const TOY_DEFINITIONS = [
  { id: "toy-bodoque", modelPath: "/peluches/bodoque.glb", position: [59.86, 0.3, -11.37] },
  { id: "toy-late", modelPath: "/peluches/late.glb", position: [10.71, 32.8, 7.08] },
  { id: "toy-pelo", modelPath: "/peluches/pelo.glb", position: [-46.47, 0.3, -12.91] },
  { id: "toy-rata-uriel", modelPath: "/peluches/rata_uriel.glb", position: [-6.08, 2.6, 49.99] },
  { id: "toy-uriel", modelPath: "/peluches/uriel.glb", position: [50.95, 0.3, 40.52] },
];

const TENT_POSITION = Object.freeze([62, -0.98, -15]);
const TACOS_POSITION = Object.freeze([-43, -1, 10.86]);
const BALLOON_DOG_POSITION = Object.freeze([47.37, 0.8, 4.05]);
const SKELETON_GREETER_POSITION = Object.freeze([-45.04, -0.7, 12]);
const MOKEY_POSITION = Object.freeze([-44.07, 2, -19.12]);
const RUFFLES_CAT_POSITION = Object.freeze([60, 1.5, -14.2]);
const BANANA_CAT_POSITION = Object.freeze([5.39, 12.5, -65.8]);
const ORBSMAN_POSITION = Object.freeze([-22.76, 11.8, -48.23]);
const POP_CAT_POSITION = Object.freeze([-4.89, -0.7, -11.86]);
const SKELETON_COMPANION_POSITION = Object.freeze([-4.36, -1, 41.72]);

const CHESS_PIECES = Object.freeze([
  { key: "chess-bishop", Component: ChessBishop, position: Object.freeze([51.99, -1, -31.2]), props: { scale: 1 } },
  { key: "chess-pawn", Component: ChessPawn, position: Object.freeze([-19.57, 1, 15.7]), props: { scale: 0.8 } },
  { key: "chess-rook", Component: ChessRook, position: Object.freeze([-32.49, -1, 37.28]), props: {} },
  { key: "chess-king", Component: ChessKing, position: Object.freeze([-2.4, 4.3, -39.6]), props: { scale: 0.7 } },
  { key: "chess-knight", Component: ChessKnight, position: Object.freeze([11.16, 3.7, 5.7]), props: { scale: 0.6 } },
  { key: "chess-queen", Component: ChessQueen, position: Object.freeze([58.74, -1, 29.81]), props: {} },
]);

const TEAPOT_SET = Object.freeze([
  { key: "teapot-base", position: Object.freeze([30, 12, -18]), props: { scale: 2 } },
  { key: "teapot-red", position: Object.freeze([1.17, 20, -0.91]), props: { scale: 4, color: "#F06968", startFrame: 90 } },
  { key: "teapot-blue", position: Object.freeze([-26.22, 8, 29.3]), props: { scale: 2, color: "#008DF6", startFrame: 60 } },
]);

const CUBE_MODELS = Object.freeze([
  { key: "cube-east", position: Object.freeze([15.8, 15, 25.7]), props: { scale: 2 } },
  { key: "cube-west", position: Object.freeze([-19, 18, -34.88]), props: { scale: 3 } },
]);

export default function Experience() {
  /**
   * Delay physics activate
   */
  const [pausedPhysics, setPausedPhysics] = useState(true);
  const gl = useThree((state) => state.gl);
  const performanceTier = usePerformanceSettings((state) => state.tier);
  const shadowDefaultsRef = useRef(null);

  const hasRecordedPositionRef = useRef(false);
  const resumeTimeoutRef = useRef(null);
  const [heavyMachineGunPosition, setHeavyMachineGunPosition] = useState(() => [...HEAVY_MACHINE_GUN_INITIAL_POSITION]);
  const [heavyMachineGunSpawnKey, setHeavyMachineGunSpawnKey] = useState(0);
  const [heavyMachineGunAvailable, setHeavyMachineGunAvailable] = useState(true);
  const [fallDamageLockActive, setFallDamageLockActive] = useState(false);
  const setHasHeavyMachineGun = useInventory((state) => state.setHasHeavyMachineGun);
  const setGunMode = useInventory((state) => state.setGunMode);
  const setIsShooting = useInventory((state) => state.setIsShooting);
  const hasHeavyMachineGun = useInventory((state) => state.hasHeavyMachineGun);
  const gunMode = useInventory((state) => state.gunMode);

  const clearResumeTimeout = useCallback(() => {
    if (resumeTimeoutRef.current !== null) {
      clearTimeout(resumeTimeoutRef.current);
      resumeTimeoutRef.current = null;
    }
  }, []);

  const applyLastKnownPosition = useCallback(() => {
    if (!hasRecordedPositionRef.current) return;

    const body = characterRef.current?.group;
    if (!body || typeof body.setTranslation !== "function") return;

    const currentTranslation = typeof body.translation === "function" ? body.translation() : null;
    const lastKnownPosition = previousPositionRef.current;

    if (!currentTranslation || !lastKnownPosition) return;

    const fellBelow = currentTranslation.y < lastKnownPosition.y - 0.5;
    if (!fellBelow) return;

    if (!Number.isFinite(lastKnownPosition.x) || !Number.isFinite(lastKnownPosition.y) || !Number.isFinite(lastKnownPosition.z)) return;

    body.setTranslation({
      x: lastKnownPosition.x,
      y: lastKnownPosition.y,
      z: lastKnownPosition.z,
    }, true);
    body.setLinvel({ x: 0, y: 0, z: 0 }, true);
    body.setAngvel({ x: 0, y: 0, z: 0 }, true);
  }, []);

  const scheduleResumePhysics = useCallback(
    (delay = 100) => {
      if (typeof window === "undefined") return;

      clearResumeTimeout();
      resumeTimeoutRef.current = window.setTimeout(() => {
        applyLastKnownPosition();
        setPausedPhysics(false);
      }, delay);
    },
    [applyLastKnownPosition, clearResumeTimeout]
  );

  const pausePhysics = useCallback(() => {
    clearResumeTimeout();
    setPausedPhysics(true);
  }, [clearResumeTimeout]);

  // Restore the collectible if the inventory was reset while the gun is unavailable.
  useEffect(() => {
    scheduleResumePhysics(500);

    return () => {
      clearResumeTimeout();
    };
  }, [scheduleResumePhysics, clearResumeTimeout]);

  useEffect(() => {
    if (!gl) return;

    if (!shadowDefaultsRef.current) {
      shadowDefaultsRef.current = {
        enabled: gl.shadowMap.enabled,
        autoUpdate: gl.shadowMap.autoUpdate,
      };
    }

    const defaults = shadowDefaultsRef.current;

    if (performanceTier === "low") {
      gl.shadowMap.enabled = false;
      gl.shadowMap.autoUpdate = false;
    } else if (performanceTier === "medium") {
      gl.shadowMap.enabled = defaults.enabled;
      gl.shadowMap.autoUpdate = false;
    } else {
      gl.shadowMap.enabled = defaults.enabled;
      gl.shadowMap.autoUpdate = defaults.autoUpdate;
    }

    return () => {
      if (!gl || !shadowDefaultsRef.current) return;
      gl.shadowMap.enabled = shadowDefaultsRef.current.enabled;
      gl.shadowMap.autoUpdate = shadowDefaultsRef.current.autoUpdate;
    };
  }, [gl, performanceTier]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") return;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        pausePhysics();
        return;
      }
      scheduleResumePhysics();
    };

    const handleBlur = () => {
      pausePhysics();
    };

    const handleFocus = () => {
      scheduleResumePhysics();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleBlur);
    window.addEventListener("focus", handleFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("focus", handleFocus);
    };
  }, [pausePhysics, scheduleResumePhysics]);

  const characterRef = useRef(null);
  const previousPositionRef = useRef({ x: 0, y: 0, z: 0 });
  const freezePositionRef = useRef(null);
  const initialGravityScaleRef = useRef(null);
  const freezeAppliedRef = useRef(false);
  const lastReportedPositionRef = useRef({ x: 0, y: 0, z: 0 });
  const fallStartTimeRef = useRef(null);
  const fallDamageReadyRef = useRef(false);
  const fallDamageLockTimeoutRef = useRef(null);
  const fallDamageLockActiveRef = useRef(false);
  const dropOrigin = useMemo(() => new THREE.Vector3(), []);
  const dropForward = useMemo(() => new THREE.Vector3(), []);
  const dropQuaternion = useMemo(() => new THREE.Quaternion(), []);

  // Drop the heavy machine gun in front of the player and make the collectible available again.
  const handleGunDrop = useCallback(() => {
    if (!gunMode || !hasHeavyMachineGun) return;

    const body = characterRef.current?.group;
    if (!body) return;

    const translation = typeof body.translation === "function" ? body.translation() : null;
    if (!translation) return;

    dropOrigin.set(translation.x, translation.y, translation.z);

    const rotation = typeof body.rotation === "function" ? body.rotation() : null;
    dropForward.set(0, 0, -1);

    if (rotation) {
      dropQuaternion.set(rotation.x, rotation.y, rotation.z, rotation.w);
      dropForward.applyQuaternion(dropQuaternion);
    }

    dropForward.y = 0;
    if (dropForward.lengthSq() < 1e-6) {
      dropForward.set(0, 0, -1);
    }
    dropForward.normalize();

  dropOrigin.addScaledVector(dropForward, 2);

    const spawnPosition = [dropOrigin.x, dropOrigin.y, dropOrigin.z];

    setHasHeavyMachineGun(false);
    setGunMode(false);
    setIsShooting(false);
    setHeavyMachineGunPosition(spawnPosition);
    setHeavyMachineGunSpawnKey((key) => key + 1);
    setHeavyMachineGunAvailable(true);
  }, [
    gunMode,
    hasHeavyMachineGun,
    characterRef,
    dropOrigin,
    dropForward,
    dropQuaternion,
    setHasHeavyMachineGun,
    setGunMode,
    setIsShooting,
    setHeavyMachineGunPosition,
    setHeavyMachineGunSpawnKey,
    setHeavyMachineGunAvailable,
  ]);

  // While in gun mode, capture the E key to trigger the drop action before other listeners run.
  useEffect(() => {
    if (!gunMode) return;

    const handleKeyDown = (event) => {
      if (event.repeat) return;
      if (event.code !== "KeyE" && event.key?.toLowerCase() !== "e") return;
      if (!useInventory.getState().gunMode) return;

      if (typeof event.stopImmediatePropagation === "function") {
        event.stopImmediatePropagation();
      } else {
        event.stopPropagation();
      }

      event.preventDefault();
      handleGunDrop();
    };

    window.addEventListener("keydown", handleKeyDown, true);

    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [gunMode, handleGunDrop]);

  useEffect(() => {
    if (hasHeavyMachineGun || gunMode || heavyMachineGunAvailable) return;

    setHeavyMachineGunPosition(() => [...HEAVY_MACHINE_GUN_INITIAL_POSITION]);
    setHeavyMachineGunSpawnKey((key) => key + 1);
    setHeavyMachineGunAvailable(true);
  }, [
    hasHeavyMachineGun,
    gunMode,
    heavyMachineGunAvailable,
    setHeavyMachineGunPosition,
    setHeavyMachineGunSpawnKey,
    setHeavyMachineGunAvailable,
  ]);

  const [, setCharacterPositionControls] = useControls(
    "Character Position",
    () => ({
      x: { value: 0, disabled: true, step: 0.01 },
      y: { value: 0, disabled: true, step: 0.01 },
      z: { value: 0, disabled: true, step: 0.01 },
      copyPosition: button(() => {
        const { x, y, z } = previousPositionRef.current || {};
        if (x === undefined || y === undefined || z === undefined) return;

        const coordinates = `[${x}, ${y}, ${z}]`;
        if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
          navigator.clipboard.writeText(coordinates).catch(() => {
            console.warn("Failed to copy character position to clipboard.");
          });
        }
      }),
    }),
    { collapsed: false }
  );

  useFrame(() => {
    const body = characterRef.current?.group;
    if (!body) return;

    const translation = body.translation?.();
    if (!translation) return;

    if (pausedPhysics) {
      fallStartTimeRef.current = null;
      fallDamageReadyRef.current = false;
    }

    if (playerFrozen) {
      if (!freezeAppliedRef.current) {
        if (initialGravityScaleRef.current === null && typeof body.gravityScale === "function") {
          initialGravityScaleRef.current = body.gravityScale();
        }
        const frozen = {
          x: translation.x,
          y: translation.y,
          z: translation.z,
        };
        freezePositionRef.current = frozen;
        body.setTranslation(frozen, true);
        body.setLinvel({ x: 0, y: 0, z: 0 }, true);
        body.setAngvel({ x: 0, y: 0, z: 0 }, true);
        body.setGravityScale(0, true);
        freezeAppliedRef.current = true;
      } else if (freezePositionRef.current) {
        body.setTranslation(freezePositionRef.current, true);
        body.setLinvel({ x: 0, y: 0, z: 0 }, true);
        body.setAngvel({ x: 0, y: 0, z: 0 }, true);
      }

      const frozen = freezePositionRef.current ?? {
        x: translation.x,
        y: translation.y,
        z: translation.z,
      };

      const formattedPosition = {
        x: Number(frozen.x.toFixed(2)),
        y: Number(frozen.y.toFixed(2)),
        z: Number(frozen.z.toFixed(2)),
      };

      previousPositionRef.current = formattedPosition;
      hasRecordedPositionRef.current = true;
      fallStartTimeRef.current = null;
      fallDamageReadyRef.current = false;
      return;
    }

    if (freezeAppliedRef.current) {
      const gravityScale = initialGravityScaleRef.current ?? 1;
      body.setGravityScale(gravityScale, true);
      freezePositionRef.current = null;
      freezeAppliedRef.current = false;
    }

    const formattedPosition = {
      x: Number(translation.x.toFixed(2)),
      y: Number(translation.y.toFixed(2)),
      z: Number(translation.z.toFixed(2)),
    };

    const linvel = typeof body.linvel === "function" ? body.linvel() : null;
    const userData = (body.userData ?? {});
    const isGrounded = Boolean(userData?.canJump);
    const isInAir = !isGrounded;
    const verticalVelocity = linvel?.y ?? 0;
    const isDescending = verticalVelocity < MIN_FALL_SPEED;
    const now = typeof performance !== "undefined" ? performance.now() : Date.now();

    if (isInAir) {
      if (isDescending) {
        if (fallStartTimeRef.current === null) {
          fallStartTimeRef.current = now;
          fallDamageReadyRef.current = false;
        } else if (!fallDamageReadyRef.current && now - fallStartTimeRef.current >= FALL_DAMAGE_THRESHOLD_MS) {
          fallDamageReadyRef.current = true;
        }
      } else if (!fallDamageReadyRef.current) {
        fallStartTimeRef.current = null;
      }
    } else if (fallStartTimeRef.current !== null) {
      if (fallDamageReadyRef.current) {
        applyFallDamage("fall");
        lockMovementAfterFallDamage();
      }
      fallStartTimeRef.current = null;
      fallDamageReadyRef.current = false;
    }

    const { x: px, y: py, z: pz } = previousPositionRef.current;
    if (formattedPosition.x === px && formattedPosition.y === py && formattedPosition.z === pz) return;

    previousPositionRef.current = formattedPosition;
    hasRecordedPositionRef.current = true;
  });

  useEffect(() => {
    if (typeof setCharacterPositionControls !== "function") return;
    if (typeof window === "undefined") return;

    const syncControls = () => {
      const latest = previousPositionRef.current;
      const lastReported = lastReportedPositionRef.current;

      if (!latest) return;

      if (latest.x !== lastReported.x || latest.y !== lastReported.y || latest.z !== lastReported.z) {
        setCharacterPositionControls({ ...latest });
        lastReportedPositionRef.current = { ...latest };
      }
    };

    const intervalId = window.setInterval(syncControls, 100);
    syncControls();

    return () => {
      window.clearInterval(intervalId);
    };
  }, [setCharacterPositionControls]);
  /**
   * Debug settings
   */
  const { physics, disableControl, disableFollowCam, devMode, performanceCulling } = useControls("World Settings", {
    physics: false,
    disableControl: false,
    disableFollowCam: false,
    performanceCulling: { label: "performance-culling", value: true },
    devMode: { label: "dev-mode", value: false },
  });
  const movementLocked = useInteractionStore((state) => state.movementLocked);
  const playerFrozen = useInteractionStore((state) => state.playerFrozen);
  const cameraLocked = useInteractionStore((state) => state.cameraLocked);
  const cameraOverride = useInteractionStore((state) => state.cameraOverride);
  const applyFallDamage = usePlayerHealth((state) => state.takeDamage);
  const toyCollected = useToyCollection((state) => state.collectedCount);
  const toyTotal = useToyCollection((state) => state.totalToys);

  const lockMovementAfterFallDamage = useCallback(() => {
    if (typeof window === "undefined") return;

    if (fallDamageLockTimeoutRef.current !== null) {
      window.clearTimeout(fallDamageLockTimeoutRef.current);
      fallDamageLockTimeoutRef.current = null;
    }

    fallDamageLockActiveRef.current = true;
    setFallDamageLockActive(true);

    fallDamageLockTimeoutRef.current = window.setTimeout(() => {
      fallDamageLockTimeoutRef.current = null;
      fallDamageLockActiveRef.current = false;
      setFallDamageLockActive(false);
    }, FALL_DAMAGE_LOCK_DURATION_MS);
  }, [setFallDamageLockActive]);

  useEffect(() => {
    return () => {
      if (typeof window !== "undefined" && fallDamageLockTimeoutRef.current !== null) {
        window.clearTimeout(fallDamageLockTimeoutRef.current);
        fallDamageLockTimeoutRef.current = null;
      }
      fallDamageLockActiveRef.current = false;
    };
  }, []);

  const cameraOverridePosition = cameraOverride?.position
    ? { x: cameraOverride.position[0], y: cameraOverride.position[1], z: cameraOverride.position[2] }
    : null;
  const cameraOverrideTarget = cameraOverride?.target
    ? { x: cameraOverride.target[0], y: cameraOverride.target[1], z: cameraOverride.target[2] }
    : null;
  const cameraOverrideTransition = cameraOverride?.transitionDuration;

  const {
    enablePixelation,
    pixelSize,
    normalEdgeStrength,
    depthEdgeStrength,
  } = useControls("Pixelation", {
    enablePixelation: true,
    pixelSize: { value: 3, min: 1, max: 24, step: 1 },
    normalEdgeStrength: { value: 0.1, min: 0, max: 1, step: 0.05 },
    depthEdgeStrength: { value: 0.2, min: 0, max: 1, step: 0.05 },
  });

  /**
   * Keyboard control preset
   */
  const keyboardMap = [
    { name: "forward", keys: ["ArrowUp", "KeyW"] },
    { name: "backward", keys: ["ArrowDown", "KeyS"] },
    { name: "leftward", keys: ["ArrowLeft", "KeyA"] },
    { name: "rightward", keys: ["ArrowRight", "KeyD"] },
    { name: "jump", keys: ["Space"] },
    { name: "run", keys: ["Shift"] },
    { name: "flyDown", keys: ["ControlLeft", "ControlRight"] },
    { name: "action1", keys: ["1"] },
    { name: "action2", keys: ["2"] },
    { name: "action3", keys: ["3"] },
    { name: "action4", keys: ["KeyF"] },
  ];

  return (
    <>
      <Perf position="top-left" minimal />
      <Lights />

      <Physics debug={physics} timeStep={1 / 60} paused={pausedPhysics}>
        <MapScene />
        <Table position={[21, -0.07, 41]} scale={.1} rotation={[0, -.5, 0]} />
        <Paper position={[3.6, -0.7, 41.76]} />
        <Gifts position={[15, 1.3, 48]} scale={38} />
        <TV position={[42, -1, 46.67]} scale={.3} rotation={[0, -.5, 0]} />
        {TEAPOT_SET.map((teapot) => (
          <DistanceCulled
            key={teapot.key}
            enabled={performanceCulling}
            targetRef={characterRef}
            position={teapot.position}
            enterDistance={42}
            exitDistance={54}
            updateInterval={0.4}
          >
            {() => (
              <Teapot
                position={teapot.position}
                {...teapot.props}
              />
            )}
          </DistanceCulled>
        ))}
        {CUBE_MODELS.map((cube) => (
          <DistanceCulled
            key={cube.key}
            enabled={performanceCulling}
            targetRef={characterRef}
            position={cube.position}
            enterDistance={38}
            exitDistance={48}
            updateInterval={0.4}
          >
            {() => (
              <CubeModel
                position={cube.position}
                {...cube.props}
              />
            )}
          </DistanceCulled>
        ))}
        <MinecraftTorch position={[9.9, 34.08, 9.45]} rotation={[0, 0, Math.PI * .1]} />
        <MinecraftTorch position={[9.9, 34.08, 4.7]} rotation={[0, 0, Math.PI * .1]} />
        {TOY_DEFINITIONS.map((toy) => (
          <ToyCollectible
            key={toy.id}
            id={toy.id}
            modelPath={toy.modelPath}
            position={toy.position}
            targetRef={characterRef}
          />
        ))}
        <ExitDoor position={[-24.75, -1.48, -49.67]} rotation={[0, Math.PI * .18, 0]} >
          <Text
            scale={.5}
            color="whites"
            maxWidth={10}
            textAlign="center"
            position={[0, 5, 2]}
            rotation={[0, 0, 0]}
          >
            {`peluches: ${toyCollected}/${toyTotal}`}
          </Text>
          <Text
            scale={.3}
            color="red"
            maxWidth={10}
            textAlign="center"
            position={[0, 3, 2]}
            rotation={[0, 0, 0]}
          >
            {`Habla con Aguacate para abrir la puerta`}
          </Text>
        </ExitDoor>
        {CHESS_PIECES.map((piece) => {
          const PieceComponent = piece.Component;
          return (
            <DistanceCulled
              key={piece.key}
              enabled={performanceCulling}
              targetRef={characterRef}
              position={piece.position}
              enterDistance={40}
              exitDistance={52}
              updateInterval={0.35}
            >
              {() => (
                <PieceComponent
                  position={piece.position}
                  {...piece.props}
                />
              )}
            </DistanceCulled>
          );
        })}
        <SignPlane position={[4.51, 12, 46]} rotation={[0, Math.PI, 0]} size={[20, 12]} />
        <DistanceCulled
          enabled={performanceCulling}
          targetRef={characterRef}
          position={TENT_POSITION}
          enterDistance={55}
          exitDistance={70}
          updateInterval={0.5}
        >
          {() => (
            <Tent position={TENT_POSITION} scale={4} rotation={[0, Math.PI + .3, 0]} />
          )}
        </DistanceCulled>
        <DistanceCulled
          enabled={performanceCulling}
          targetRef={characterRef}
          position={TACOS_POSITION}
          enterDistance={48}
          exitDistance={60}
          updateInterval={0.5}
        >
          {() => (
            <Tacos position={TACOS_POSITION} scale={2} rotation={[0, Math.PI * 1.5, 0]} />
          )}
        </DistanceCulled>
        <DistanceCulled
          enabled={performanceCulling}
          targetRef={characterRef}
          position={BALLOON_DOG_POSITION}
          enterDistance={42}
          exitDistance={52}
          updateInterval={0.4}
        >
          {() => (
            <BalloonDog position={BALLOON_DOG_POSITION} scale={.5} rotation={[0, Math.PI * 1.5, 0]} />
          )}
        </DistanceCulled>
        <DistanceCulled
          enabled={performanceCulling}
          targetRef={characterRef}
          position={SKELETON_GREETER_POSITION}
          enterDistance={44}
          exitDistance={56}
          updateInterval={0.4}
        >
          {() => (
            <SkeletonGreeter
              position={SKELETON_GREETER_POSITION}
              rotation={[0, 0, 0]}
              scale={2}
              targetRef={characterRef}
            />
          )}
        </DistanceCulled>
        <DistanceCulled
          enabled={performanceCulling}
          targetRef={characterRef}
          position={MOKEY_POSITION}
          enterDistance={44}
          exitDistance={56}
          updateInterval={0.4}
        >
          {() => (
            <Mokey position={MOKEY_POSITION} targetRef={characterRef} />
          )}
        </DistanceCulled>
        <DistanceCulled
          enabled={performanceCulling}
          targetRef={characterRef}
          position={RUFFLES_CAT_POSITION}
          enterDistance={46}
          exitDistance={58}
          updateInterval={0.35}
        >
          {() => (
            <RufflesCat position={RUFFLES_CAT_POSITION} targetRef={characterRef} />
          )}
        </DistanceCulled>
        <DistanceCulled
          enabled={performanceCulling}
          targetRef={characterRef}
          position={POP_CAT_POSITION}
          enterDistance={32}
          exitDistance={40}
          updateInterval={0.35}
        >
          {() => (
            <PopCat position={POP_CAT_POSITION} scale={.15} rotation={[0, Math.PI * -.8, 0]} targetRef={characterRef} />
          )}
        </DistanceCulled>
        {heavyMachineGunAvailable && (
          <HeavyMachineGun
            key={heavyMachineGunSpawnKey}
            position={heavyMachineGunPosition}
            targetRef={characterRef}
            radius={1}
            scaleMultiplier={0.5}
            onCollect={() => {
              setHeavyMachineGunAvailable(false);
            }}
          />
        )}
        <Cake
          initialPosition={[5.72, -3.2, 46.53]}
          targetPosition={[5.72, 1.2, 46.53]}
          scale={2}
          raiseDelay={0.4}
          raiseDuration={0.8}
        />
        <Confetti scale={2} />
        <Balloons position={[-3.24, 1, 46.16]} scale={0.09} />
        <CakeTrigger
          targetRef={characterRef}
          cakePosition={[5.72, 0, 46.53]}
          focusPosition={[5.72, 1.2, 46.53]}
          radius={30}
          cameraOffset={[5.72, 9.2, 16.53]}
        />
        <DistanceCulled
          enabled={performanceCulling}
          targetRef={characterRef}
          position={BANANA_CAT_POSITION}
          enterDistance={60}
          exitDistance={75}
          updateInterval={0.4}
        >
          {() => (
            <BananaCat
              position={BANANA_CAT_POSITION}
              rotation={[0, 3, 0]}
              scale={1.5}
              targetRef={characterRef}
            />
          )}
        </DistanceCulled>
        <DistanceCulled
          enabled={performanceCulling}
          targetRef={characterRef}
          position={ORBSMAN_POSITION}
          enterDistance={55}
          exitDistance={68}
          updateInterval={0.4}
        >
          {() => (
            <Orbsman
              position={ORBSMAN_POSITION}
              rotation={[0, 0, 0]}
              scale={.25}
              targetRef={characterRef}
            />
          )}
        </DistanceCulled>
        <DistanceCulled
          enabled={performanceCulling}
          targetRef={characterRef}
          position={SKELETON_COMPANION_POSITION}
          enterDistance={36}
          exitDistance={44}
          updateInterval={0.35}
        >
          {() => (
            <SkeletonCompanion
              position={SKELETON_COMPANION_POSITION}
              rotation={[0, 0, 0]}
              scale={1}
              targetRef={characterRef}
            />
          )}
        </DistanceCulled>
        <DanceFloor position={[4.71, -0.4, 33.51]} scale={1.3} />
        <BackupSkeletons />

        {/* Keyboard preset */}
        <KeyboardControls map={keyboardMap}>
          {/* Character Control */}
          <Ecctrl
            ref={characterRef}
            debug
            animated
            followLight
            springK={2}
            dampingC={0.2}
            autoBalanceSpringK={1.2}
            autoBalanceDampingC={0.04}
            autoBalanceSpringOnY={0.7}
            autoBalanceDampingOnY={0.05}
            disableControl={disableControl || movementLocked || fallDamageLockActive}
            disableFollowCam={disableFollowCam || cameraLocked}
            disableFollowCamPos={cameraLocked ? cameraOverridePosition : null}
            disableFollowCamTarget={cameraLocked ? cameraOverrideTarget : null}
            disableFollowCamTransition={cameraOverrideTransition}
            camCollision={!cameraLocked}
            devModeEnabled={devMode}
            position={[7.7, 15, -122.26]}
          >
            {/* Replace your model here */}
            <CharacterModel />
          </Ecctrl>
        </KeyboardControls>

        {/* Slopes and stairs */}
        <Slopes />
        <Stairs position={[-4.57, 8.74, 6.11]} rotation={[0, Math.PI * 1.3, 0]} />
        <Stairway position={[0, 27, 5]} scale={100} color="#4DE284" />

        {/* Rigid body objects */}
        <RigidObjects />

        {/* Floating platform */}
        <FloatingPlatform />

        {/* Dynamic platforms */}
        <DynamicPlatforms />

        {/* Shoting cubes */}
        <ShotCube />
      </Physics>

      {enablePixelation && (
        <PixelationEffect
          pixelSize={pixelSize}
          normalEdgeStrength={normalEdgeStrength}
          depthEdgeStrength={depthEdgeStrength}
        />
      )}
    </>
  );
}
